import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import {
  ThemeSwitcher,
  LanguageSwitcher,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui'
import { LocationPicker } from '@/components/checkout'
import { MapView, type LocationMarker } from '@/components/map/MapView'
import { PickupTimePill } from '@/components/layout/PickupTimePill'
import { useMenuData } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'

export function StickyTopBar() {
  const { t } = useTranslation()
  const { restaurant } = useMenuData()
  const { address, menuLocations } = useSelectedLocation()
  const [showLocationDialog, setShowLocationDialog] = useState(false)
  const [showAddressSheet, setShowAddressSheet] = useState(false)

  const isMultiLocation = menuLocations.length >= 2

  const locationMarkers = useMemo<LocationMarker[]>(() =>
    menuLocations
      .filter((l): l is typeof l & { latitude: number; longitude: number } =>
        l.latitude != null && l.longitude != null
      )
      .map((l) => ({ id: l.id, lat: l.latitude, lng: l.longitude, label: l.location_name })),
    [menuLocations]
  )

  const primaryCoords = locationMarkers[0] ?? null

  return (
    <>
      <div className="sticky top-0 z-40 w-full bg-background border-b border-border/40">
          <div className="container mx-auto max-w-7xl px-3 sm:px-4 flex items-center gap-2 sm:gap-3 h-14 sm:h-[60px]">
            {/* Left: location address */}
            <div className="flex items-center min-w-0">
              {/* Mobile: pin icon → bottom sheet */}
              <button
                type="button"
                onClick={() => setShowAddressSheet(true)}
                className="inline-flex sm:hidden items-center justify-center h-10 w-10 min-h-[44px] min-w-[44px] rounded-full border border-border bg-muted/50 hover:bg-muted text-muted-foreground transition-colors cursor-pointer"
              >
                <MapPin className="h-4 w-4" />
              </button>

              {/* Desktop: inline address pill */}
              {isMultiLocation ? (
                <button
                  type="button"
                  onClick={() => setShowLocationDialog(true)}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-full border border-border bg-muted/50 hover:bg-muted text-sm text-foreground transition-colors cursor-pointer max-w-full"
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{address}</span>
                  <span className="shrink-0 text-primary font-semibold text-xs">
                    {t('common:change')}
                  </span>
                </button>
              ) : (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground truncate max-w-full">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{address}</span>
                </span>
              )}
            </div>

            <div className="flex-1" />

            {/* Right: pickup time + language + theme */}
            <div className="flex items-center gap-1 shrink-0">
              <PickupTimePill />
              <LanguageSwitcher />
              <ThemeSwitcher />
            </div>
          </div>
        </div>

        {/* Mobile: address bottom sheet */}
        <Sheet open={showAddressSheet} onOpenChange={setShowAddressSheet}>
          <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-8">
            <SheetHeader className="mb-4">
              <SheetTitle>{t('common:pickupLocation')}</SheetTitle>
            </SheetHeader>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm text-foreground">{address}</p>
                {isMultiLocation && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddressSheet(false)
                      setShowLocationDialog(true)
                    }}
                    className="text-sm text-primary font-semibold mt-2 hover:underline"
                  >
                    {t('common:change')}
                  </button>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Location change dialog */}
        {isMultiLocation && (
          <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg overflow-hidden p-0" hideCloseButton>
              {primaryCoords && (
                <div className="w-full aspect-[3/2] overflow-hidden">
                  <MapView
                    isStatic
                    restaurantCoords={primaryCoords}
                    locations={locationMarkers}
                    className="w-full h-full"
                  />
                </div>
              )}
              <div className="p-4 sm:p-6">
                <DialogTitle className="text-base font-semibold mb-3">
                  {t('common:choosePickupLocation')}
                </DialogTitle>
                <LocationPicker
                  locations={menuLocations}
                  restaurant={restaurant}
                  hideTitle
                  onSelect={() => setShowLocationDialog(false)}
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    )
  }
